import {
    waiting_for_prompt_and_teacher,
    waiting_for_data,
    gathering_data,
    ready_for_distillation,
} from '@/shared/constants/taskStatuses';
import { TaskType } from '@/shared/domain/taskTypes';
import { normalizeWizardStep, wizardStepsFor } from '@/features/tasks/components/CreationWizard/WizardStepper';
import { agentConfigHasTools } from '@/features/tasks/components/CreationWizard/agentConfig/agentConfigModel';

/**
 * Local registry of "draft" tasks — tasks created by the creation wizard
 * (`/create-task`) but whose wizard flow was exited before training started.
 *
 * The wizard writes the current step here on every step change and removes
 * the entry when training kicks off, so the Projects page can render the
 * task as a draft card and clicking it reopens the wizard at the exact step
 * the user left.
 *
 * Stored in localStorage (per browser) because the backend has no draft
 * concept: the usecase itself is fully persisted server-side from step 1 on.
 */
const STORAGE_KEY = 'specificai.taskDrafts.v1';

/**
 * Statuses a task can have while still inside the creation wizard. Once a
 * task progresses past these (training started, deployed, ...) a leftover
 * registry entry no longer marks it as a draft.
 */
const SETUP_STATUSES = new Set([
    waiting_for_prompt_and_teacher,
    waiting_for_data,
    gathering_data,
    ready_for_distillation,
]);

const readRegistry = () => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const writeRegistry = (registry) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(registry));
    } catch {
        // Storage unavailable (private mode / quota) — drafts just aren't remembered.
    }
};

/**
 * Record (or refresh) the wizard step a draft task was left on. Other fields
 * of the entry (per-task flags such as `benchmarkAcknowledged`) are kept.
 */
export const saveTaskDraft = (usecaseId, step) => {
    if (!usecaseId || !step) return;
    const registry = readRegistry();
    registry[usecaseId] = { ...(registry[usecaseId] || {}), step, updatedAt: Date.now() };
    writeRegistry(registry);
};

/**
 * Remember a per-task wizard choice on the draft entry, e.g. that the user
 * already continued past the "Continue without a benchmark?" prompt. A task
 * without an entry yet gets one without a step (the wizard stamps the step
 * on its next step change).
 */
export const setTaskDraftFlag = (usecaseId, flag, value) => {
    if (!usecaseId || !flag) return;
    const registry = readRegistry();
    registry[usecaseId] = { ...(registry[usecaseId] || {}), [flag]: value };
    writeRegistry(registry);
};

/** Read a per-task flag saved with `setTaskDraftFlag` (`undefined` when unset). */
export const getTaskDraftFlag = (usecaseId, flag) => {
    if (!usecaseId || !flag) return undefined;
    return readRegistry()[usecaseId]?.[flag];
};

/** Forget a draft (wizard finished, or the task no longer qualifies). */
export const removeTaskDraft = (usecaseId) => {
    if (!usecaseId) return;
    const registry = readRegistry();
    if (!(usecaseId in registry)) return;
    delete registry[usecaseId];
    writeRegistry(registry);
};

/**
 * Whether the task progressed past the creation wizard (training started,
 * deployed, ...). A missing status still counts as in-setup: without a
 * status we cannot prove the wizard was finished.
 */
export const isPastWizardSetup = (usecase) =>
    Boolean(usecase?.status && !SETUP_STATUSES.has(usecase.status));

/**
 * Draft entry (`{ step, updatedAt }`) for a usecase row, or `null` when the
 * task is not a draft. A registry entry is ignored once the task progressed
 * past the setup statuses (e.g. training was started outside the wizard).
 */
export const getTaskDraft = (usecase) => {
    if (!usecase?._id) return null;
    const entry = readRegistry()[usecase._id];
    if (!entry?.step) return null;
    if (isPastWizardSetup(usecase)) return null;
    return entry;
};

/** Wizard URL that resumes a draft task at its saved step. */
export const draftWizardUrl = (usecaseId, step) => {
    const params = new URLSearchParams({ step, usecaseId });
    return `/create-task?${params.toString()}`;
};

/** Wizard step the Review Training Plan opens from (`normalizeWizardStep` → Model Setup). */
export const DRAFT_READY_STEP = 'train';

/**
 * Whether each wizard step is complete, judged from what the task has saved
 * server-side rather than from where the user left the wizard:
 *
 * - Business Goals: a task type, plus at least one label / entity for
 *   classification / NER (the step's Next requires both).
 * - Agent Config (agentic tasks only): at least one tool.
 * - Data Collection: a dataset attached, or a data flow (upload, Generate
 *   with AI, Retrieval Agent) still running for the task — the non-blocking
 *   upload attaches the clone only when it lands.
 * - Model Setup: a saved prompt (autosaved while the user edits it).
 *
 * @param {Object} usecase - usecase row from `/llm_usecases`
 * @param {{ hasActiveDataFlow?: boolean }} [options]
 * @returns {Array<{ key: string, label: string }>} steps not complete yet, in wizard order
 */
export const getDraftMissingSteps = (usecase, { hasActiveDataFlow = false } = {}) => {
    const steps = wizardStepsFor(usecase?.autonomy_level);
    const taskType = usecase?.task_type;
    const labels =
        taskType === TaskType.NER
            ? usecase?.manually_added_entities
            : taskType === TaskType.classification
              ? usecase?.manually_added_labels
              : null;
    const needsLabels = taskType === TaskType.NER || taskType === TaskType.classification;
    const complete = {
        'business-goals': Boolean(taskType) && (!needsLabels || (labels || []).length > 0),
        'agent-config': agentConfigHasTools(usecase?.agent_config),
        'data-collection': (usecase?.datasets || []).length > 0 || hasActiveDataFlow,
        'model-setup': Boolean(String(usecase?.prompt || '').trim()),
    };
    return steps.filter((step) => !complete[step.key]);
};

/**
 * Labels of the wizard steps a draft has not completed yet, for the draft
 * card's "Steps missing: ...". Empty when the task is ready to train.
 */
export const getDraftMissingStepLabels = (usecase, options) =>
    getDraftMissingSteps(usecase, options).map((step) => step.label);

/**
 * Wizard step a draft resumes at: `train` (Model Setup with Review Training
 * Plan open) once nothing is missing; otherwise the saved step, or the first
 * missing step when that comes earlier (a dataset that was cancelled after
 * the user moved on must be re-added before Model Setup makes sense). An
 * unknown saved step falls back to the first missing one.
 */
export const draftResumeStep = (draft, missingSteps, autonomyLevel = null) => {
    if (!missingSteps?.length) return DRAFT_READY_STEP;
    const steps = wizardStepsFor(autonomyLevel);
    const savedIdx = steps.findIndex((s) => s.key === normalizeWizardStep(draft?.step));
    const firstMissingIdx = steps.findIndex((s) => s.key === missingSteps[0].key);
    if (savedIdx < 0) return missingSteps[0].key;
    return steps[Math.min(savedIdx, firstMissingIdx < 0 ? savedIdx : firstMissingIdx)].key;
};
